import { useEffect, useState } from 'react'

import { AppMsg } from 'cat-shared/utils'
import { QuestionSubmitFormValues } from 'client/utils'
import { useGetQuestion, useCreateAnswer } from 'client/api'
import { displaySnackBar } from 'client/services'
import type { TGetQuestionData } from 'client/api/useGetQuestion/useGetQuestion'

type ReturnObj = {
  data: TGetQuestionData
  fetchQuestion: () => void
  isQuestionBeingFetched: boolean
  isQuestionBeingSubmitted: boolean
  isQuestionError: boolean
  handleQuestionSubmission: (formValues: QuestionSubmitFormValues) => Promise<unknown>
}

export const useQuestion = (id: string, disableInitFetch = false): ReturnObj => {
  const [isQuestionBeingFetched, setIsQuestionBeingFetched] = useState<boolean>(false)
  const [isQuestionBeingSubmitted, setIsQuestionBeingSubmitted] = useState<boolean>(false)
  const [isQuestionError, setIsQuestionError] = useState<boolean>(false)

  const getQuestionQuery = useGetQuestion(id)
  const createAnswerQuery = useCreateAnswer()

  useEffect(() => {
    if (!disableInitFetch && id) {
      fetchQuestion()
    }
  }, [id])

  const fetchQuestion = async () => {
    setIsQuestionError(false)
    setIsQuestionBeingFetched(true)
    await getQuestionQuery
      .refetch()
      .then(() => {
        setIsQuestionBeingFetched(false)
      })
      .catch(err => {
        setIsQuestionBeingFetched(false)
        handleFetchQuestionError(err)
      })
  }

  const handleQuestionSubmission = async (
    formValues: QuestionSubmitFormValues
  ): Promise<unknown> => {
    return new Promise((resolve, reject) => {
      const run = async () => {
        setIsQuestionBeingSubmitted(true)
        await createAnswerQuery.mutateAsync({ questionId: id, formValues })
        await getQuestionQuery
          .refetch()
          .then(() => {
            setIsQuestionBeingSubmitted(false)
            resolve(true)
          })
          .catch(err => {
            setIsQuestionBeingSubmitted(false)
            handleFetchQuestionError(err)
            reject(err)
          })
      }
      run()
    })
  }

  const handleFetchQuestionError = (err: { msg: AppMsg }) => {
    setIsQuestionError(true)
    displaySnackBar(err.msg)
  }

  return {
    data: getQuestionQuery.data,
    fetchQuestion,
    isQuestionBeingFetched,
    isQuestionBeingSubmitted,
    isQuestionError,
    handleQuestionSubmission,
  }
}
